import { generatePath, Link } from 'react-router'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'

import { URL_PAGE_CATALOG_1ST_LVL } from '@shared/contants'
import { CATEGORY_DEFAULT_ICON_PATH } from '@shared/contants/product'
import { cn } from '@shared/lib/cn'

import { useCategorySlugs } from './parts/useCategorySlugs'

import styles from './LandingCategoryLinksBlock.module.scss'

/*
  category_links: curated shortcuts into catalog categories, addressed by
  categoryId. Ids are resolved to catalog slugs (see useCategorySlugs); items
  whose category is unavailable for the current shop are dropped, and the section
  disappears when nothing is left.

  Note: the school event landing moved these shortcuts into a `menu` block, but
  the block type stays supported — it is part of the landing contract.
*/
interface CategoryLinkPayloadItem {
  label?: string;
  categoryId?: string | number;
  image?: string;
}

interface CategoryLinksPayload {
  title?: string;
  name?: string;
  layout?: string;
  items?: CategoryLinkPayloadItem[];
}

export default function LandingCategoryLinksBlock ({ payload, shopId }: LandingBlockProps) {
  const { title, name, items } = (payload ?? {}) as CategoryLinksPayload

  const payloadItems = (items ?? []).filter((item) => item?.categoryId != null)
  const categoryIds = payloadItems.map((item) => String(item.categoryId))

  const { categoriesById, loading } = useCategorySlugs(categoryIds, shopId)

  /*
    The payload carries the label (and often the image), so an item is displayable
    on its own — the category lookup only supplies the slug the catalog route
    needs. Items whose category does not resolve for the current shop stay
    visible but inert instead of disappearing from a curated list.
  */
  const links = payloadItems.map((item) => {
    const category = categoriesById.get(String(item.categoryId))

    return {
      key: String(item.categoryId),
      label: item.label || category?.name || '',
      image: item.image || category?.iconUrl || '',
      href: category?.slug
        ? generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug: category.slug })
        : undefined,
    }
  })

  const heading = title || name

  if (loading || links.length === 0) {
    return null
  }

  return (
    <section className={styles.root}>
      {heading && <h2 className={styles.root__title}>{heading}</h2>}

      <Swiper
        className={styles.root__slider}
        slidesPerView="auto"
        spaceBetween={12}
        modules={[ Navigation ]}
        navigation
      >
        {links.map((link) => {
          const body = (
            <>
              <span className={styles.item__media}>
                <img
                  className={cn(styles.item__image, {
                    [styles['item__image--plug']]: !link.image,
                  })}
                  src={link.image || CATEGORY_DEFAULT_ICON_PATH}
                  alt=""
                  loading="lazy"
                />
              </span>
              <span className={styles.item__label}>{link.label}</span>
            </>
          )

          return (
            <SwiperSlide className={styles.root__slide} key={link.key}>
              {link.href
                ? <Link className={styles.item} to={link.href}>{body}</Link>
                : <span className={styles.item}>{body}</span>}
            </SwiperSlide>
          )
        })}
      </Swiper>
    </section>
  )
}
